import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const terms = [
  {
    title: '1. Acceptance of these terms',
    text: 'By accessing or using the Goshen Prinath Academy website, mobile application or connected services, you agree to these Terms of Service and the Privacy Policy. If you do not agree, you should not use the platform.',
  },
  {
    title: '2. Authorised educational use',
    text: 'The platform is provided for authorised academic and administrative activities. Users must use only the account and role assigned to them and must provide accurate information when requested.',
  },
  {
    title: '3. Accounts and security',
    text: 'You are responsible for protecting your login credentials and for activity performed through your account. Do not share access tokens or passwords. Notify the Academy promptly if you suspect unauthorised access or misuse.',
  },
  {
    title: '4. Academic information and results',
    text: 'Results, attendance records, remarks and other academic information displayed through the platform are school records. If information appears incorrect, users should contact the Academy through the appropriate official channel. Users must not alter, falsify or misuse academic records.',
  },
  {
    title: '5. Acceptable conduct',
    text: 'You must not attempt to gain unauthorised access, disrupt the service, upload harmful material, impersonate another person, scrape protected information, bypass security controls, misuse another user’s data or use the platform for unlawful or abusive activity.',
  },
  {
    title: '6. Content and intellectual property',
    text: 'The platform, its design, software, branding and Academy-provided materials are protected by applicable intellectual-property rights. Access is granted only for permitted educational and administrative use and does not transfer ownership to the user.',
  },
  {
    title: '7. Privacy',
    text: 'Use of the platform is also governed by our Privacy Policy, which explains how personal and academic information is collected, used, protected, retained and disclosed.',
  },
  {
    title: '8. Availability and changes',
    text: 'We aim to keep the service accurate, secure and available, but temporary interruptions may occur because of maintenance, connectivity, upgrades or events beyond our reasonable control. Features may be improved, replaced or discontinued when necessary.',
  },
  {
    title: '9. Suspension or termination',
    text: 'The Academy may restrict or terminate access where an account is no longer authorised, these terms are violated, security is at risk or restriction is reasonably required for legal, academic or administrative reasons.',
  },
  {
    title: '10. Disclaimer and responsibility',
    text: 'The service is provided for school operations and is made available to the extent permitted by applicable law. The Academy is not responsible for loss caused by a user’s unauthorised conduct, compromised credentials, unsupported devices or external network failures.',
  },
  {
    title: '11. Changes to these terms',
    text: 'These terms may be updated when the service, school procedures or applicable obligations change. Updated terms will be published on this page with a revised effective date. Continued use after an update constitutes acceptance where permitted by law.',
  },
];

const TermsOfService = () => {
  useEffect(() => {
    document.title = 'Terms of Service | Goshen Prinath Academy';
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="text-sm font-semibold text-primary hover:opacity-80">
            Goshen Prinath Academy
          </Link>
          <Link
            to="/login-authentication"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-primary hover:text-primary"
          >
            Back to login
          </Link>
        </div>
      </header>

      <section className="border-b border-slate-200 bg-gradient-to-br from-primary/10 via-white to-secondary/10">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Legal
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Terms of Service</h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            These terms govern authorised access to and use of the Goshen Prinath Academy digital platform.
          </p>
          <p className="mt-4 text-sm font-medium text-slate-500">Effective date: 12 August 2026</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-9 p-6 sm:p-10">
            {terms.map((term) => (
              <section key={term.title}>
                <h2 className="text-xl font-semibold text-slate-900">{term.title}</h2>
                <p className="mt-3 leading-7 text-slate-600">{term.text}</p>
                {term.title === '7. Privacy' && (
                  <Link to="/privacy-policy" className="mt-3 inline-flex font-semibold text-primary hover:underline">
                    Read the Privacy Policy
                  </Link>
                )}
              </section>
            ))}

            <section className="rounded-xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
              <h2 className="text-xl font-semibold text-slate-900">12. Contact us</h2>
              <p className="mt-3 leading-7 text-slate-600">
                Questions about these terms should be directed to Goshen Prinath Academy through its official
                communication channels or contact page.
              </p>
              <Link to="/contact" className="mt-4 inline-flex font-semibold text-primary hover:underline">
                Contact the Academy
              </Link>
            </section>
          </div>
        </article>
      </div>
    </main>
  );
};

export default TermsOfService;
